/**
 * Память экрана «Организатор» в браузере (задача 35, этап D): последний показанный экран
 * и его версия (`ETag`).
 *
 * Зачем: повторное открытие раздела не должно ждать сети и не должно перерисовывать
 * таблицы, если данные не изменились. Поэтому таблицы рисуются сразу из памяти, а затем
 * идёт перепроверка версии: ответ `304` означает «перерисовывать нечего», а изменённые
 * данные приходят телом и заменяют сохранённый экран.
 *
 * Память живёт в пределах вкладки и **привязана к токену сессии**: после выхода и входа
 * другого пользователя сохранённый экран не показывается (иначе чужому организатору
 * мелькнули бы данные прежней сессии).
 */
import type { OrganizerScreen } from '../shared/types';
import { fetchOrganizerScreen } from './api';
import { getSessionToken } from './session';

/** Сохранённый экран и его версия; версии нет — перепроверка всегда будет полной. */
interface CachedScreen {
  /** Токен сессии, которой экран принадлежит. */
  token: string | null;
  screen: OrganizerScreen;
  etag?: string;
}

let cached: CachedScreen | undefined;

/** Сохранённый экран, если он принадлежит текущей сессии: SPA рисует его сразу. */
export function cachedOrganizerScreen(): OrganizerScreen | undefined {
  return cached && cached.token === getSessionToken() ? cached.screen : undefined;
}

/**
 * Открывает раздел: сначала показывает сохранённый экран (если он есть), затем
 * перепроверяет версию на сервере. `draw` вызывается один раз для сохранённого экрана и
 * один — для свежего; на `304` второй вызов не делается, таблицы уже нарисованы.
 */
export async function refreshOrganizerScreen(
  draw: (screen: OrganizerScreen) => void,
): Promise<void> {
  const previous = cached && cached.token === getSessionToken() ? cached : undefined;
  if (previous) draw(previous.screen);

  const response = await fetchOrganizerScreen(previous?.etag);
  if (response.notModified || !response.screen) return;

  cached = { token: getSessionToken(), screen: response.screen, etag: response.etag };
  draw(response.screen);
}
